import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import type { ErrorLike } from "@apollo/client";
import type { Color } from "@salestrak/ui";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { WidgetIssueStageDwellDocument, isStageType, type StageType } from "#app/lib/data";
import {
  useWorkspaceData,
  type WorkspaceStage,
} from "#app/lib/data/workspace-data-provider";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { combineWhereConditions } from "#app/lib/query-utils";
import { STAGE_TYPE_COLOR, STAGE_TYPE_ORDER } from "#app/lib/stage-type";

/** What one band stands for: a stage, or a whole stage category. */
export type StageFunnelRows = "stage" | "stageType";

/** One band of the funnel. `avgSeconds` is the mean time the projects NOW in
 *  the step have waited there so far; null when nothing sits in it. */
export type StageFunnelRow = {
  id: string;
  label: string;
  color: Color | null;
  count: number;
  overdue: number;
  avgSeconds: number | null;
};

export type StageFunnel = {
  /** The steps, in pipeline order. */
  open: StageFunnelRow[];
  /** Where the pipeline ends: the done and canceled rows. */
  closed: StageFunnelRow[];
  /** Every project in scope, open and closed — the base a closed row's share is read against. */
  total: number;
};

/** The two stage categories a project leaves the pipeline through. */
const CLOSED_TYPES: ReadonlySet<string> = new Set<StageType>(["done", "canceled"]);

type DwellStat = { stageId: string; avgSeconds: number; issues: number };

/**
 * Fold the three per-stage reads into funnel rows. Pure, so the widget's shape
 * decisions are testable without a provider:
 *
 * - `rows: "stage"` keeps every stage of `stages` in the order given. An empty
 *   stage stays when `keepEmpty` (one workflow was chosen, so its pipeline is
 *   complete and "nothing in Build scheduled" is a fact) and drops otherwise
 *   (stages of every workflow mixed together, where an empty row is noise).
 * - `rows: "stageType"` always yields the four categories: the vocabulary is
 *   fixed, and a funnel of categories with a step missing misreads.
 * - Dwell rolls up to a category as an issue-weighted mean, never a mean of means.
 */
export function buildStageFunnel(input: {
  rows: StageFunnelRows;
  stages: readonly WorkspaceStage[];
  keepEmpty: boolean;
  totals: Record<string, number>;
  overdue: Record<string, number>;
  dwell: readonly DwellStat[];
}): StageFunnel {
  const dwellByStage = new Map(input.dwell.map((d) => [d.stageId, d]));
  const total = input.stages.reduce((sum, s) => sum + (input.totals[s.stageId] ?? 0), 0);

  if (input.rows === "stage") {
    const open: StageFunnelRow[] = [];
    const closed: StageFunnelRow[] = [];
    for (const s of input.stages) {
      const count = input.totals[s.stageId] ?? 0;
      if (count === 0 && !input.keepEmpty) continue;
      const d = dwellByStage.get(s.stageId);
      const row: StageFunnelRow = {
        id: s.stageId,
        label: s.name,
        color: (s.icon?.color as Color | null | undefined) ?? null,
        count,
        overdue: input.overdue[s.stageId] ?? 0,
        avgSeconds: d && d.issues > 0 ? d.avgSeconds : null,
      };
      (CLOSED_TYPES.has(s.type ?? "") ? closed : open).push(row);
    }
    return { open, closed, total };
  }

  const byType = new Map<StageType, { count: number; overdue: number; dwellSum: number; dwellIssues: number }>();
  for (const type of STAGE_TYPE_ORDER) byType.set(type, { count: 0, overdue: 0, dwellSum: 0, dwellIssues: 0 });
  for (const s of input.stages) {
    if (!isStageType(s.type)) continue;
    const bucket = byType.get(s.type);
    if (!bucket) continue;
    bucket.count += input.totals[s.stageId] ?? 0;
    bucket.overdue += input.overdue[s.stageId] ?? 0;
    const d = dwellByStage.get(s.stageId);
    if (d && d.issues > 0) {
      bucket.dwellSum += d.avgSeconds * d.issues;
      bucket.dwellIssues += d.issues;
    }
  }
  const open: StageFunnelRow[] = [];
  const closed: StageFunnelRow[] = [];
  for (const type of STAGE_TYPE_ORDER) {
    const bucket = byType.get(type);
    if (!bucket) continue;
    const row: StageFunnelRow = {
      id: type,
      label: stageTypeLabel(type) ?? type,
      color: STAGE_TYPE_COLOR[type],
      count: bucket.count,
      overdue: bucket.overdue,
      avgSeconds: bucket.dwellIssues > 0 ? bucket.dwellSum / bucket.dwellIssues : null,
    };
    (CLOSED_TYPES.has(type) ? closed : open).push(row);
  }
  return { open, closed, total };
}

/**
 * The funnel's data: three exact reads over the scoped predicate, folded per
 * stage or per stage category. Per-stage project counts and the Stale subset
 * come from `issueGroupCounts` (the same fan-out the throughput card uses, on
 * one age status), time in stage from `issueStageDwell` with `currentOnly`:
 * the wait so far of the projects sitting in the step, not the historical
 * length of a stay (Rob, 2026-09-08 — "the 175 projects sitting there"). The
 * widget's Filters and its workflow choice AND into the dashboard's own scope;
 * the card never re-plumbs the window. The workflow the rows are read for is
 * the widget's own choice, else the workflow the dashboard is pinned to: in a
 * workflow's Dashboard tab the funnel lists that workflow's stages, empties
 * included, rather than every stage in the workspace with the empty ones
 * dropped.
 */
export function useStageFunnel(
  rows: StageFunnelRows,
  chosenWorkflowId: string | null | undefined,
  filters: unknown,
): { data: StageFunnel; loading: boolean; error?: ErrorLike | undefined } {
  const scope = useDashboardScope();
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : null;
  const workflowId = chosenWorkflowId ?? scopeWorkflowId;
  const { base: filterBase, override } = useWidgetFilters(filters);
  const base = useMemo(
    () =>
      combineWhereConditions([
        filterBase,
        ...(workflowId ? [{ workflowId: { eq: workflowId } }] : []),
      ]) ?? {},
    [filterBase, workflowId],
  );
  const { where } = useScopedWhere(base, { override });
  const { stages, stagesByWorkflow } = useWorkspaceData();

  const overdueWhere = useMemo(
    () => ({ AND: [where, { age: { status: { eq: "offTrack" } } }] }),
    [where],
  );
  const totals = useIssueGroupCounts({ where, groupBy: "stageId" });
  const overdue = useIssueGroupCounts({ where: overdueWhere, groupBy: "stageId" });
  const dwellVariables = useMemo(() => ({ where, currentOnly: true }), [where]);
  const dwell = useQuery(WidgetIssueStageDwellDocument, {
    variables: dwellVariables,
    fetchPolicy: "cache-and-network",
  });

  const dwellReady = dwell.data !== undefined;
  const loading = !totals.ready || !overdue.ready || !dwellReady;
  const error = totals.error ?? overdue.error ?? dwell.error;

  const scopedStages = useMemo(
    () => (workflowId ? (stagesByWorkflow.get(workflowId) ?? []) : stages),
    [workflowId, stages, stagesByWorkflow],
  );

  const data = useMemo<StageFunnel>(
    () =>
      buildStageFunnel({
        rows,
        stages: scopedStages,
        keepEmpty: Boolean(workflowId),
        totals: totals.counts,
        overdue: overdue.counts,
        dwell: dwell.data?.issueStageDwell ?? [],
      }),
    [rows, scopedStages, workflowId, totals.counts, overdue.counts, dwell.data],
  );

  return { data, loading, error };
}
